/**
 * Lightweight global store for the data-freshness gate.
 *
 * The backend caps a section's date range to the freshest day all of its
 * source tables have loaded, and returns `{ watermark, gated }` in the response
 * `meta`. The API client publishes that here whenever it sees it, and the
 * report header subscribes to show a "Data as of <date>" indicator.
 */
import { useSyncExternalStore } from 'react';

export interface DataFreshness {
  /** Freshest day (YYYY-MM-DD) served, or null when undeterminable. */
  watermark: string | null;
  /** True when the requested end date was capped back to the watermark. */
  gated: boolean;
}

let current: DataFreshness | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Publish the latest freshness (called by the API client on gated responses). */
export function setDataFreshness(next: DataFreshness | null): void {
  current = next;
  emit();
}

/** Clear the indicator (e.g. when entering a section before any query runs). */
export function resetDataFreshness(): void {
  setDataFreshness(null);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): DataFreshness | null {
  return current;
}

/** React hook: the current data-freshness gate state, or null. */
export function useDataFreshness(): DataFreshness | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Inspect a response `meta` and publish freshness when the gate is present.
 * A gate response always carries a `gated` boolean; other endpoints (pagination,
 * counts) are ignored so they don't clobber the indicator.
 */
export function captureFreshnessFromMeta(meta: Record<string, unknown> | undefined): void {
  if (!meta || !('gated' in meta)) return;
  setDataFreshness({
    watermark: typeof meta.watermark === 'string' ? meta.watermark : null,
    gated: Boolean(meta.gated),
  });
}
